<template>
  <div class="flex-center position-ref">
    <Loading :loading="loading"></Loading>
    <div class="content">
      <img src="/assets/logos/k12net.png" alt="K12Net" />
      <template v-if="platformLogo">
        <img src="/assets/logos/arrow.jpg" alt="" />
        <img :src="platformLogo" :alt="platform" />
      </template>
      <br /><br /><br />
      <h3 v-if="error" class="k12net-error">{{ error }}</h3>
      <h3 v-else>{{ status }}</h3>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import axios from "axios";
import CryptoJS from "crypto-js";
import { K12NET_PLATFORMS, k12netRedirectUri } from "@/config/k12net";
import { serviceApiBaseUrl } from "@/config/serviceApi";

/**
 * K12Net SSO dönüş adımı.
 *
 * K12Net tüm platformlar için bu tek adrese döner; hedef platformu `state` taşır.
 *
 * Akışın tamamı artık sunucuda (`/sso/k12net/resolve`) çözülür. Eskiden burada altı
 * ayrı istek zinciri vardı ve `client_credentials` partner token'ı tarayıcıya iniyordu;
 * ayrıca veli girişinde UES kontrolü hiç yapılmadığı için her girişte koşulsuz kullanıcı
 * oluşturma yolu çalışıyordu. Bkz. ueslms/.claude/notes/k12net-entegrasyonu.md
 */
export default {
  name: "K12Net",
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      platform: null,
      status: 'Lütfen bekleyiniz, ilgili platforma yönlendiriliyorsunuz.',
      error: null,
    }
  },
  computed: {
    platformConfig() {
      return this.platform ? (K12NET_PLATFORMS[this.platform] || null) : null;
    },
    platformLogo() {
      return this.platformConfig ? '/assets/logos/' + this.platformConfig.logo : null;
    },
  },
  beforeMount() {
    const query = this.$route.query;

    // Eskiden bu kontroller yoktu: parametre eksikse ekran sonsuza kadar
    // "bekleyiniz" yazıp duruyor, kullanıcı neyin yanlış olduğunu göremiyordu.
    if (!query.code) {
      this.fail('K12Net yönlendirmesi eksik geldi (code parametresi yok). Lütfen K12Net üzerinden tekrar giriş yapın.');
      return;
    }

    if (!query.state) {
      this.fail('Hedef platform belirtilmemiş (state parametresi yok).');
      return;
    }

    this.platform = query.state;

    if (!this.platformConfig) {
      this.fail(`Tanımsız platform: ${query.state}`);
      return;
    }

    this.resolve(query.code);
  },
  methods: {
    fail(message) {
      this.loading = false;
      this.error = message;
      console.log('K12Net SSO:', message);
    },

    /**
     * Tek istek: code → (K12Net doğrulama + UES kullanıcısı) → payload.
     * Sunucu organizasyon ağacı kurabildiği için timeout cömert tutulur;
     * yine de sınırsız değil, aksi hâlde hata durumunda ekran kilitleniyor.
     */
    async resolve(code) {
      this.loading = true;

      try {
        const response = await axios.post(`${serviceApiBaseUrl}/sso/k12net/resolve`, {
          code: code,
          redirect_uri: k12netRedirectUri(),
          platform: this.platform,
        }, { timeout: 90000 });

        const data = response.data;

        if (!data || !data.success || !data.user || !data.user.user_id) {
          this.fail('Kullanıcı bilgileri alınamadı. Lütfen tekrar deneyin.');
          return;
        }

        this.redirectToPlatform(data.user);
      } catch (error) {
        // Sunucu anlamlı bir mesaj döndüyse onu göster; yoksa genel mesaj.
        const message = error.response && error.response.data && error.response.data.message;
        this.fail(message || 'Bağlantı kurulamadı. Lütfen tekrar deneyin.');
      }
    },

    redirectToPlatform(user) {
      const config = this.platformConfig;

      // decodeURIComponent kaldırıldı: JSON içinde geçerli bir yüzde dizisi olmayan
      // tek bir "%" karakteri (sınıf/okul adında) URIError fırlatıyor ve SSO'yu
      // tamamen düşürüyordu. Hedef bileşenler zaten simetrik bir encode yapmıyor,
      // "%" içermeyen payload'larda çıktı birebir aynı.
      const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(user), config.secretKey).toString();

      this.status = 'Yönlendiriliyorsunuz…';
      this.loading = false;

      window.location.href = config.redirectUrl + btoa(ciphertext);
    },
  }
}
</script>

<style scoped>
html, body {
  /* background-color: #37474f; */
  background-color: #712481;
  height: 100vh;
  margin: 0;
}

.flex-center {
  align-items: center;
  display: flex;
  justify-content: center;
}

.position-ref {
  position: relative;
}

.content {
  margin-top: 50px;
  text-align: center;
}

.k12net-error {
  max-width: 520px;
  margin: 0 auto;
  color: #ffd6d6;
}
</style>
